import { FC, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  Box,
  Checkbox,
  Collapse,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
} from "@mui/material";
import __ from "src/utils/Translations";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { absencesAbsencesConfigurationWrite } from "src/utils/RoleBasedSecurity/permissions";
import translateLabels from "src/utils/Translations/translateLabels";
import CustomRadioGroup from "src/Pages/Company/Absences/Forms/CustomRadioGroup";
import { ApproversType, IAbsenceTypeForm } from "./AbsenceType.models";

interface IApproversConfigurationProps {
  disabled: boolean;
}
const ApproversConfiguration: FC<IApproversConfigurationProps> = ({
  disabled,
}) => {
  const {
    formState: { errors },
    setValue,
  } = useFormContext<IAbsenceTypeForm>();

  const chosenApproversType = useWatch({ name: "approversType" });
  const chosenApprovers = useWatch({ name: "approvers" });

  const visible = chosenApproversType === ApproversType.MANUAL;

  const approversTypeOptions = useMemo(
    () => [
      { label: "Akceptacja automatyczna", value: ApproversType.AUTO },
      {
        label: "Akceptacja przez akceptanta",
        value: ApproversType.MANUAL,
      },
    ],
    []
  );

  const handleOptionChange = (option: string) => {
    const newArray = [...chosenApprovers];
    if (chosenApprovers.includes(option)) {
      setValue(
        "approvers",
        newArray.filter((o: string) => o !== option)
      );
    } else {
      newArray.push(option);
      setValue("approvers", newArray);
    }
  };

  const handleSuperiorChange = (checked: boolean) => {
    const newArray = [...chosenApprovers];
    if (checked) {
      newArray.push("IMMEDIATE_SUPERIOR");
      newArray.push("INTERMEDIATE_SUPERIOR");
      setValue("approvers", newArray);
    } else {
      setValue(
        "approvers",
        newArray.filter(
          (o: string) =>
            !["IMMEDIATE_SUPERIOR", "INTERMEDIATE_SUPERIOR"].includes(o)
        )
      );
    }
  };

  return (
    <>
      <CustomRadioGroup
        id="approversType"
        name="approversType"
        label={__("Sposób akceptacji wniosku")}
        options={translateLabels(approversTypeOptions)}
        disabled={disabled}
        dataTestId="absence-type-form-field__approvers-type"
      />
      <Collapse in={visible}>
        <FormGroup
          sx={{
            bgcolor: "grey.100",
            padding: "1rem 2rem",
            marginTop: "1rem",
            borderRadius: "0.5rem",
          }}
        >
          <FormLabel
            component="legend"
            sx={{ fontWeight: 700, color: "text.primary" }}
          >
            {__("Wybór akceptantów")}
          </FormLabel>
          {errors?.approvers && (
            <FormHelperText error>{errors.approvers?.message}</FormHelperText>
          )}
          <FormControlLabel
            label={__("Przełożeni")}
            control={
              <Checkbox
                checked={
                  chosenApprovers.includes("IMMEDIATE_SUPERIOR") &&
                  chosenApprovers.includes("INTERMEDIATE_SUPERIOR")
                }
                indeterminate={
                  chosenApprovers.includes("IMMEDIATE_SUPERIOR") !==
                  chosenApprovers.includes("INTERMEDIATE_SUPERIOR")
                }
                data-testid="absence-type-form-field__approvers"
              />
            }
            onChange={(_event, checked) => handleSuperiorChange(checked)}
            disabled={disabled}
          />
          <Box sx={{ display: "flex", flexDirection: "column", ml: 3 }}>
            <FormControlLabel
              label={__("Przełożony bezpośredni")}
              control={
                <Checkbox
                  checked={chosenApprovers.includes("IMMEDIATE_SUPERIOR")}
                  onChange={() => handleOptionChange("IMMEDIATE_SUPERIOR")}
                  data-testid="absence-type-form-field__approvers-direct"
                />
              }
              disabled={disabled}
            />
            <FormControlLabel
              label={__("Przełożeni wyższego stopnia")}
              control={
                <Checkbox
                  checked={chosenApprovers.includes("INTERMEDIATE_SUPERIOR")}
                  onChange={() => handleOptionChange("INTERMEDIATE_SUPERIOR")}
                  data-testid="absence-type-form-field__approvers-intermediate"
                />
              }
              disabled={disabled}
            />
          </Box>
          <FormControlLabel
            label={__("AHR")}
            control={
              <Checkbox
                checked={chosenApprovers.includes("AHR")}
                onChange={() => handleOptionChange("AHR")}
                data-testid="absence-type-form-field__ahr"
              />
            }
            disabled={disabled}
          />
        </FormGroup>
      </Collapse>
    </>
  );
};

export default ApproversConfiguration;
